import {computed, reactive} from 'vue';
import {defineStore} from 'pinia';
import {useAppStore} from "@/stores/app";
import {WorkerResponseCode} from "../utilities/workerUtilites.js";
import {EndpointsFactory} from "../utilities/endpoints.js";

export const useDomStore = defineStore('dom', () => {
  const serverData = reactive({
    serverStatusLabels: {},
    serverStatuses: [],
    serverProgressionLabels: {},
    serverProgressions: [],
    serverProgressionSubstatusLabels: {},
    serverProgressionSubstatuses: [],
    serverProvinces: [],
    serverNotificationLabels: {},
    serverNotifications: []
  });

  const statuses = computed(() => {
    return serverData.serverStatuses.map(c => {
      return {
        value: c[DOM_STATUSES_FIELDS.ID],
        title: serverData.serverStatusLabels[c[DOM_STATUSES_FIELDS.LBL_KEY]],
        color: c[DOM_STATUSES_FIELDS.COLOR]
      };
    });
  });

  const progressions = computed(() => {
    return serverData.serverProgressions.map(c => {
      return {
        value: c[DOM_PROGRESSIONS_FIELDS.ID],
        title: serverData.serverProgressionLabels[c[DOM_PROGRESSIONS_FIELDS.LBL_KEY]],
        icon: c[DOM_PROGRESSIONS_FIELDS.ICON],
        effectiveEndDateLabel: serverData.serverProgressionLabels[c[DOM_PROGRESSIONS_FIELDS.LBL_KEY] + import.meta.env.VITE_EFFECTIVE_END_DATE_ORDER_PHASE_LABEL_SUFFIX]
      };
    });
  });

  const notifications = computed(() => {
    return serverData.serverNotifications.map(c => {
      return {
        value: c[DOM_NOTIFICATIONS_FIELDS.ID],
        title: serverData.serverNotificationLabels[c[DOM_NOTIFICATIONS_FIELDS.LBL_KEY]]
      };
    });
  });

  const provinces = computed(() => {
    return serverData.serverProvinces.map(c => {
      return {
        value: c[DOM_PROVINCES_FIELDS.CODE],
        title: c[DOM_PROVINCES_FIELDS.PLATE]
      };
    });
  });

  const progressionSubstatuses = computed(() => {
    return serverData.serverProgressionSubstatuses.map(c => {
      return {
        value: c[DOM_PROGRESSION_SUBSTATUSES_FIELDS.ID],
        title: serverData.serverProgressionSubstatusLabels[c[DOM_PROGRESSION_SUBSTATUSES_FIELDS.LBL_KEY]],
        color: c[DOM_PROGRESSION_SUBSTATUSES_FIELDS.COLOR]
      };
    });
  });

  function reset(){
    serverData.serverStatusLabels = {};
    serverData.serverStatuses = [];
    serverData.serverProgressionLabels = {};
    serverData.serverProgressions = [];
    serverData.serverProgressionSubstatusLabels = {};
    serverData.serverProgressionSubstatuses = [];
    serverData.serverProvinces = [];
    serverData.serverNotificationLabels = {};
    serverData.serverNotifications = [];
  }

  function __fetchEntity(token, serverDataEntity, endpoint, responseCode){
    return new Promise((resolve, reject) => {
      if(serverData[serverDataEntity] && serverData[serverDataEntity].length){ //aggiorno solo se non le ho
        return resolve();
      }
      EndpointsFactory.execApiViaWorker({
          url: EndpointsFactory.getEndpoint(endpoint),
          token: token,
          method: 'GET'
        },
        responseCode, ({result}) => {
          serverData[serverDataEntity] = result;
          resolve()
        }, () => {
          reject();
        });
    });
  }

  function __fetchEntityLabels(lang, token, serverLabelsEntity, labelPrefix, response){
    return new Promise((resolve, reject) => {
      if(serverData[serverLabelsEntity] && Object.keys(serverData[serverLabelsEntity]).length){ //aggiorno solo se non le ho
        return resolve();
      }
      EndpointsFactory.execApiViaWorker({
        url: EndpointsFactory.getEndpoint(EndpointsFactory.ENDPOINTS.LABELS_GET, {
          language_code: lang,
          labels_prefix: labelPrefix
        }),
        token: token,
        method: 'GET'
      }, response, ({result}) => {
        serverData[serverLabelsEntity] = result;
        resolve();
      }, () => {
        reject();
      });
    });
  }

  function _fetchStatusLabels(currentLang, currentToken){
    return __fetchEntityLabels(currentLang, currentToken, "serverStatusLabels", "dom_ta_statuses", WorkerResponseCode.LABELS_GET_STATUSES);
  }

  function _fetchStatuses(currentToken){
    return __fetchEntity(currentToken, "serverStatuses", EndpointsFactory.ENDPOINTS.DOM_GET_STATUSES, WorkerResponseCode.DOM_GET_STATUSES);
  }

  function _fetchProgressionsLabels(currentLang, currentToken){
    return __fetchEntityLabels(currentLang, currentToken, "serverProgressionLabels", "dom_ta_progression_status", WorkerResponseCode.LABELS_GET_PROGRESSIONS);
  }

  function _fetchProgressions(currentToken){
    return __fetchEntity(currentToken, "serverProgressions", EndpointsFactory.ENDPOINTS.DOM_GET_PROGRESSIONS, WorkerResponseCode.DOM_GET_PROGRESSIONS);
  }

  function _fetchProgressionSubstatusesLabels(currentLang, currentToken){
    return __fetchEntityLabels(currentLang, currentToken, "serverProgressionSubstatusLabels", "dom_ta_progression_substatus", WorkerResponseCode.LABELS_GET_PROGRESSION_SUBSTATUSES);
  }

  function _fetchProgressionSubstatuses(currentToken){
    return __fetchEntity(currentToken, "serverProgressionSubstatuses", EndpointsFactory.ENDPOINTS.DOM_GET_PROGRESSION_SUBSTATUSES, WorkerResponseCode.DOM_GET_PROGRESSION_SUBSTATUSES);
  }

  function _fetchProvinces(currentToken){
    return __fetchEntity(currentToken, "serverProvinces", EndpointsFactory.ENDPOINTS.DOM_GET_PROVINCES, WorkerResponseCode.DOM_GET_PROVINCES);
  }

  function _fetchNotificationsLabels(currentLang, currentToken){
    return __fetchEntityLabels(currentLang, currentToken, "serverNotificationLabels", "dom_ta_notification_events", WorkerResponseCode.LABELS_GET_NOTIFICATIONS);
  }

  function _fetchNotifications(currentToken){
    return __fetchEntity(currentToken, "serverNotifications", EndpointsFactory.ENDPOINTS.DOM_GET_NOTIFICATIONS, WorkerResponseCode.DOM_GET_NOTIFICATIONS);
  }

  async function loadDomData(currentToken, currentLang, admin = false, guest = false){
    const appStore = useAppStore();
    appStore.showSpinner(true);
    try{
      if(guest){
        await Promise.all([]);
      }
      else{
        await Promise.all([]);
        await Promise.all([
          _fetchStatuses(currentToken), _fetchStatusLabels(currentLang, currentToken),
          _fetchProgressions(currentToken), _fetchProgressionsLabels(currentLang, currentToken),
          _fetchProgressionSubstatuses(currentToken), _fetchProgressionSubstatusesLabels(currentLang, currentToken),
          _fetchNotifications(currentToken), _fetchNotificationsLabels(currentLang, currentToken),
          _fetchProvinces(currentToken)
        ]);
      }
      appStore.hideSpinner(true);
    }catch (e){
      appStore.showError();
    }
  }

  return {
    statuses,
    progressions,
    progressionSubstatuses,
    provinces,
    notifications,

    loadDomData,
    reset,
    //questo è esposto solo perché così viene salvato in locale; ma non è pensato per essere usato
    serverData
  };
}, {
  persist: true
});

const DOM_STATUSES_FIELDS = {
  ID: 'id',
  STATUS: 'status',
  LBL_KEY: 'label_key_status',
  LAST_UPDATE_BY: 'last_update_by',
  LAST_UPDATE_DATE: 'last_update_date',
  CREATED_BY: 'created_by',
  CREATION_DATE: 'creation_date',
  COLOR: 'color'
};

const DOM_PROGRESSIONS_FIELDS = {
  ID: 'id',
  PROGRESSION: 'progression_status',
  LBL_KEY: 'label_key_progression_status',
  LAST_UPDATE_BY: 'last_update_by',
  LAST_UPDATE_DATE: 'last_update_date',
  CREATED_BY: 'created_by',
  CREATION_DATE: 'creation_date',
  ICON: 'mdi_icon'
};

const DOM_NOTIFICATIONS_FIELDS = {
  ID: 'id',
  NOTIFICATION: 'notification_event',
  LBL_KEY: 'label_key_notification_event',
  LAST_UPDATE_BY: 'last_update_by',
  LAST_UPDATE_DATE: 'last_update_date',
  CREATED_BY: 'created_by',
  CREATION_DATE: 'creation_date'
};

export const DOM_PROGRESSION_SUBSTATUSES_FIELDS = {
  ID: 'id',
  PROGRESSION_SUBSTATUS: 'progression_substatus',
  LBL_KEY: 'label_key_progression_substatus',
  LAST_UPDATE_BY: 'last_update_by',
  LAST_UPDATE_DATE: 'last_update_date',
  CREATED_BY: 'created_by',
  CREATION_DATE: 'creation_date',
  COLOR: 'color'
};

const DOM_PROVINCES_FIELDS = {
  CODE: 'code',
  NAME: 'name',
  PLATE: 'plate',
  LBL_KEY: 'label_key_provinces',
  LAST_UPDATE_BY: 'last_update_by',
  LAST_UPDATE_DATE: 'last_update_date',
  CREATED_BY: 'created_by',
  CREATION_DATE: 'creation_date'
};
